import React, { useState, useEffect, useRef } from 'react';
import config from '../config/config';
import { executeSuggest } from '../services/searchService';

const HISTORY_NUM_MAX = config.search.searchHistoryMaxItems;
const DEBOUNCE_DELAY_MS = config.search.suggestDebounceMs;
const DEFAULT_RADIUS = config.api.bffParams.suggestDefaultRadius;

const KEY_CODE_IME_COMPOSITION = config.search.keyCodeImeComposition;

// アイコンURL定義
const ICON_URLS = {
  close: `${config.icons.baseUrl}${config.icons.close}`,
  keyboardBackspace: `${config.icons.baseUrl}${config.icons.keyboardBackspace}`,
  history: `${config.icons.baseUrl}${config.icons.history}`,
  spot: `${config.icons.baseUrl}${config.icons.location}`,
};

/**
 * 検索モーダルコンポーネント
 * 上に検索ヘッダ（search-modal-header）、下に最近の検索（search-history-section）
 * 検索ヘッダに入力しても、履歴から選んでも、元の検索ボックスにテキストが入る
 */
const SearchModal = ({
  map,
  searchQuery,
  setSearchQuery,
  onSubmit,
  onSuggestionSelect,
  onCloseSearchModal,
  handleCloseSearchResult,
  center
}) => {

  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);
  const lastQueryRef = useRef(''); // 前回サジェスト実行時のトリム済み文字列を保持

  // モーダル表示時に確実にフォーカスを当てる
  useEffect(() => {
    // 次フレームで確実にフォーカス（onMouseDown/onTouchStartによる阻害を回避）
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  // 入力値（searchQuery）が変わるたびにサジェストAPIを呼ぶ（trimして一致する文字列はスキップ）
  useEffect(() => {
    // 空やスペースのみならサジェスト候補をクリア、APIも呼ばない（例えば、"  "を検索しない）
    const trimmedSearchQuery = (searchQuery || '').trim();
    if (!trimmedSearchQuery) {
      setSuggestions([]);
      return;
    }
    // trimの結果、前回サジェスト時と同じ文字列なら呼ばない
    if (trimmedSearchQuery === lastQueryRef.current) {
      return;
    }

    // 入力値があればn秒後にサジェストAPIを呼び、結果をsuggestionsにセット
    const timer = setTimeout(async () => {
      const result = await executeSuggest(trimmedSearchQuery, center, DEFAULT_RADIUS, map);
      setSuggestions(result.data?.items || []);
      // 正常応答時のみ、「前回サジェスト時の文字列」を更新
      lastQueryRef.current = trimmedSearchQuery;
    }, DEBOUNCE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [searchQuery, center, map]);

  // 履歴はlocalStorageで保存/読み込み
  const getInitialHistory = () => {
    try {
      return JSON.parse(localStorage.getItem('searchHistory')) || [];
    } catch {
      return [];
    }
  };

  // 検索履歴のstate
  const [searchHistory, setSearchHistory] = useState(getInitialHistory());

  // 履歴に追加
  const addToHistory = (query) => {
    if (!query) return;
    const trimmed = String(query).trim();
    if (!trimmed) return; // 空白のみは履歴に残さない
    const newHistory = [trimmed, ...searchHistory.filter(q => q !== trimmed)].slice(0, HISTORY_NUM_MAX);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  // 履歴から削除
  const removeFromHistory = (query) => {
    const newHistory = searchHistory.filter(q => q !== query);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  // 全履歴削除
  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  // 検索実行時
  const handleSubmit = (query) => {
    if (!query) return;
    const trimmed = String(query).trim();
    if (!trimmed) return; // 空文字・空白のみなら何もしない
    addToHistory(trimmed);
    if (onSubmit) onSubmit(trimmed);
  };

  // サジェストアイテムをクリックしたときの処理
  const handleItemClick = (item) => {

    // サジェスト選択時はその名称を履歴に追加
    const label = (item.poi_name || item.name || '').trim();
    if (label) {
      addToHistory(label);
    }

    // onSuggestionSelect が渡されていれば、それを呼び出す
    if (onSuggestionSelect) {
      onSuggestionSelect(item);
    } else {
      // フォールバックとして従来の検索を実行
      const displayName = item.poi_name || item.name || '名称不明';
      onSubmit(displayName);
    }
  };

  // オーバーレイ（背景）自身が押された時だけ閉じる
  // e.target: 実際に押された要素
  // e.currentTarget: ハンドラを付けた要素（=オーバーレイ）
  const handleOverlayPointerDown = (e) => {
    const clickedOverlayItself = (e.target === e.currentTarget);
    if (!clickedOverlayItself) {
      return;
    }
    onCloseSearchModal();
  };

  return (
    <>
      {/* オーバーレイ（外部クリックで閉じるため） */}
      <div
        className="search-modal-overlay"
        onPointerDown={handleOverlayPointerDown}
      />
      {/* 検索ボックスクリック時にスマホでは全画面表示、PCでは画面左側に表示 */}
      <div id="search-modal" className="search-modal">
        <div className="search-modal-content">
          {/* 検索ヘッダー */}
          <div className="search-modal-header">
            <button
              className="search-back-btn"
              id="search-back-btn"
              onClick={onCloseSearchModal}
            >
              <i
                className="svg-icon-blue"
                style={{ backgroundImage: `url('${ICON_URLS.keyboardBackspace}')` }}>
              </i>
            </button>

            <div className="search-modal-input-container">
              <input
                type="text"
                className="search-modal-input"
                id="search-modal-input"
                placeholder="場所を検索"
                ref={inputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                // mapcode:など、URL扱いにされてtext/plainが取れない（iOSで生じる）ケース対策
                onPaste={(e) => {
                  const cd = e.clipboardData;
                  const uri = cd?.getData('text/uri-list') || '';
                  // 勝手にURL扱いされている = uri-listが取れたときだけ矯正
                  if (uri && uri.includes('%20')) {
                    e.preventDefault();
                    setSearchQuery(uri.replace(/%20/g, ' '));
                  }
                }}

                onKeyDown={e => {
                  // IME入力中（日本語変換中）はエンターキーを無視する
                  // isComposing プロパティが true の場合は日本語などの変換中
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing && e.keyCode !== KEY_CODE_IME_COMPOSITION) {
                    handleSubmit(searchQuery);
                  }
                }}
                autoFocus
              />
              {/* 1文字以上入力された時に閉じる（クリア）ボタン表示 */}
              {searchQuery && searchQuery.length > 0 && (
                <button
                  className="search-modal-input-clear-btn"
                  onClick={() => {
                    // 親のsearchQueryをクリアする
                    setSearchQuery('');
                    // クリア後もフォーカスを維持する
                    inputRef.current?.focus();
                    // 検索関連の閉じるボタン共通の、ワード・カテゴリ・結果・マップのピンをクリア
                    handleCloseSearchResult();
                  }}
                  aria-label="入力をクリア"
                >
                  <i className="svg-icon-black"
                    style={{
                      backgroundImage: `url('${ICON_URLS.close}')`
                    }}>
                  </i>
                </button>
              )}
            </div>
          </div>

          {/* サジェスト */}
          {suggestions.length > 0 && (
            <div className="suggestion-list">
              {suggestions.map((data, index) => {
                const typeName = data?.category2_name?.[0] || 'スポット';
                const typeLabel = data.taxonomy === 'address' ? '' : typeName;
                const displayName = data.poi_name || data.name || '名称不明';
                return (
                  <div
                    className="result-card clickable-result"
                    data-lat={data.lat}
                    data-lon={data.lon}
                    data-id={data.poi_code || data.id}
                    data-index={index}
                    key={data.poi_code || data.id || index}
                    onClick={() => handleItemClick(data)}
                    style={{
                      display: 'flex',
                      cursor: 'pointer',
                      padding: '8px 12px'
                    }}
                  >

                    <div className="result-header">
                      <h3 className="result-title">
                        <img
                          src={ICON_URLS.spot}
                          alt={typeLabel}
                          style={{ width: 16, height: 16, marginRight: 8 }}
                        />
                        {/* 親要素が display: flex の場合に、その子要素が「残りの空きスペースをすべて埋める」ように伸びるという指定 */}
                        <div style={{ flex: 1 }}>
                          <div>{displayName}</div>
                          {typeLabel && (
                            <div style={{ fontSize: '12px', color: '#888' }}>{typeLabel}</div>
                          )}
                        </div>
                      </h3>
                    </div>

                  </div>
                );
              })}
            </div>
          )}

          {/* 検索履歴 */}
          <div className="search-history-section">
            <div className="search-history-header">
              <h3>最近の検索</h3>
              <button
                className="clear-all-history-btn"
                id="clear-all-history-btn"
                onClick={clearHistory}
              >
                すべて削除
              </button>
            </div>
            <div className="search-history-list" id="search-history-list">

              {/* 履歴アイテムがここに動的に追加される */}
              {searchHistory.map((query, idx) => (
                <div className="search-history-item" data-query={query} key={idx}>
                  <div className="search-history-icon">
                    <i
                      className="svg-icon-black"
                      style={{
                        backgroundImage: `url('${ICON_URLS.history}')`
                      }}>
                    </i>
                  </div>
                  <div
                    className="search-history-text"
                    onClick={() => handleSubmit(query)}
                    style={{ cursor: 'pointer' }}
                  >
                    {query}
                  </div>
                  <button
                    className="history-delete-btn"
                    title="この履歴を削除"
                    onClick={() => removeFromHistory(query)}
                  >
                    <i
                      className="svg-icon-black"
                      style={{
                        backgroundImage: `url('${ICON_URLS.close}')`
                      }}
                    >
                    </i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchModal;


